/**
 * ConcentrationScatterChart — per (well × analyte) scatter + optional
 * linear regression trend lines with 12-month projection.
 * Native hand-rolled SVG (no Recharts).
 */

import { useState, useMemo, useRef, useEffect, useCallback, useId } from 'react';
import { scaleTime, scaleLinear } from 'd3-scale';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { WellSelectorPanel } from './WellSelectorPanel';
import { AnalyteSelectorPanel } from './AnalyteSelectorPanel';
import { linearRegression } from './linearRegression';
import { WELL_COLORS, ANALYTE_COLORS } from './chartTheme';
import { useRegulatoryLines } from '../hooks/useRegulatoryLines';
import { ChartTooltip } from './ChartTooltip';
import { useChartView } from './interactions/useChartView';
import { ChartTimeRangeRow } from './ChartTimeRangeRow';
import { defaultHiddenWellIds, defaultHiddenAnalytes } from './chartDefaults';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

interface ScatterPoint {
  ts: number;
  value: number;
  series: string;
  color: string;
}

interface RegSegment {
  series: string;
  color: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

const MARGIN = { top: 12, right: 24, bottom: 40, left: 64 };
const CHART_HEIGHT = 420;
const PROJECTION_MS = 365.25 * 24 * 3600 * 1000;

export function ConcentrationScatterChart({ data, unit }: Props) {
  // Scatter overlays multi-well × multi-analyte — default to showing all.
  const [hiddenWellIds, setHiddenWellIds] = useState<Set<string>>(new Set());
  const [hiddenAnalytes, setHiddenAnalytes] = useState<Set<string> | null>(null);
  const [showDots, setShowDots] = useState(true);
  const [showRegression, setShowRegression] = useState(true);

  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries()).map(([code, compound], i) => ({
      code,
      compound,
      color: ANALYTE_COLORS[i % ANALYTE_COLORS.length],
    }));
  }, [data.wells]);

  const effectiveHidden = useMemo(() => {
    if (hiddenAnalytes !== null) return hiddenAnalytes;
    return new Set<string>();
  }, [hiddenAnalytes]);

  const visibleAnalyteCodes = useMemo(
    () => new Set(allAnalytes.filter((a) => !effectiveHidden.has(a.code)).map((a) => a.code)),
    [allAnalytes, effectiveHidden],
  );

  const visibleWells = data.wells.filter((w) => !hiddenWellIds.has(w.well_id));
  const { lines: regulatoryLines } = useRegulatoryLines(data, visibleAnalyteCodes);

  const { points, regressions } = useMemo(() => {
    const pts: ScatterPoint[] = [];
    const regs: RegSegment[] = [];
    for (const well of visibleWells) {
      const wellIdx = data.wells.findIndex((w) => w.well_id === well.well_id);
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!visibleAnalyteCodes.has(code)) continue;
        const seriesKey = `${well.well_name} — ${analyte.compound}`;
        const color = visibleWells.length <= 1
          ? (allAnalytes.find((a) => a.code === code)?.color || ANALYTE_COLORS[0])
          : WELL_COLORS[wellIdx % WELL_COLORS.length];

        const regPts: Array<[number, number]> = [];
        for (const m of analyte.measurements) {
          const ts = new Date(m.date).getTime();
          const conc = convertConc(m.concentration, unit);
          if (conc === null) continue;
          pts.push({ ts, value: conc, series: seriesKey, color });
          regPts.push([ts, conc]);
        }
        if (showRegression && regPts.length >= 2) {
          const reg = linearRegression(regPts);
          if (reg) {
            const startT = regPts[0][0];
            const endT = regPts[regPts.length - 1][0] + PROJECTION_MS;
            regs.push({
              series: seriesKey,
              color,
              x0: startT,
              y0: Math.max(0, reg.predict(startT)),
              x1: endT,
              y1: Math.max(0, reg.predict(endT)),
            });
          }
        }
      }
    }
    return { points: pts, regressions: regs };
  }, [visibleWells, visibleAnalyteCodes, unit, data.wells, allAnalytes, showRegression]);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const clipId = `scatter-clip-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, CHART_HEIGHT - MARGIN.top - MARGIN.bottom);

  const baseXDomain = useMemo<readonly [number, number]>(() => {
    let lo = Infinity, hi = -Infinity;
    for (const p of points) {
      if (p.ts < lo) lo = p.ts;
      if (p.ts > hi) hi = p.ts;
    }
    for (const r of regressions) {
      if (r.x1 > hi) hi = r.x1;
    }
    if (!isFinite(lo) || !isFinite(hi)) return [0, 1];
    if (lo === hi) return [lo - 86_400_000, hi + 86_400_000];
    return [lo, hi];
  }, [points, regressions]);

  const baseYDomain = useMemo<readonly [number, number]>(() => {
    let yMax = 0;
    for (const p of points) if (p.value > yMax) yMax = p.value;
    for (const line of regulatoryLines) if (line.value > yMax) yMax = line.value;
    return [0, (yMax > 0 ? yMax : 1) * 1.1];
  }, [points, regulatoryLines]);

  const xPxToDataRef = useRef<(px: number) => number>(() => 0);
  const yPxToDataRef = useRef<(py: number) => number>(() => 0);
  const view = useChartView(
    svgRef,
    (px) => xPxToDataRef.current(px),
    (py) => yPxToDataRef.current(py),
    {
      baseXDomain,
      baseYDomain,
      dragZoomAxes: 'x',
      chartArea: {
        top: MARGIN.top,
        bottom: MARGIN.top + innerHeight,
        left: MARGIN.left,
        right: MARGIN.left + innerWidth,
      },
    },
  );

  const xScale = useMemo(
    () => scaleTime().domain([view.xDomain[0], view.xDomain[1]]).range([MARGIN.left, MARGIN.left + innerWidth]),
    [view.xDomain, innerWidth],
  );
  const yScale = useMemo(
    () => scaleLinear().domain([view.yDomain[0], view.yDomain[1]]).range([MARGIN.top + innerHeight, MARGIN.top]).nice(),
    [view.yDomain, innerHeight],
  );
  xPxToDataRef.current = (px: number) => xScale.invert(px).getTime();
  yPxToDataRef.current = (py: number) => yScale.invert(py);

  const xTicks = useMemo(() => xScale.ticks(6), [xScale]);
  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  const formatX = (d: Date) => `${d.getMonth() + 1}/${d.getFullYear().toString().slice(2)}`;
  const formatY = (v: number) => {
    if (v === 0) return '0';
    const abs = Math.abs(v);
    if (abs < 0.01) return v.toExponential(1);
    if (abs < 1) return v.toPrecision(2);
    return v.toFixed(1);
  };

  interface HoverState { point: ScatterPoint; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current; if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const lx = e.clientX - rect.left;
      const ly = e.clientY - rect.top;
      let best: ScatterPoint | null = null;
      let bestDsq = 14 * 14;
      for (const p of points) {
        const px = xScale(p.ts);
        const py = yScale(p.value);
        const dx = px - lx;
        const dy = py - ly;
        const dsq = dx * dx + dy * dy;
        if (dsq < bestDsq) {
          bestDsq = dsq;
          best = p;
        }
      }
      if (best) setHover({ point: best, clientX: e.clientX, clientY: e.clientY });
      else setHover(null);
    },
    [points, xScale, yScale],
  );
  const handleMouseLeave = useCallback(() => setHover(null), []);

  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  const wellItems = data.wells.map((w, i) => ({
    well_id: w.well_id,
    well_name: w.well_name,
    color: WELL_COLORS[i % WELL_COLORS.length],
  }));
  const visibleWellIdSet = new Set(visibleWells.map((w) => w.well_id));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-mono-500 tracking-wider">
            {visibleWells.length}/{data.wells.length} WELLS
          </span>
          <button
            onClick={() => setShowDots(!showDots)}
            className={`px-2 py-0.5 text-[10px] tracking-wider rounded transition-colors ${
              showDots ? 'bg-cyan-900/30 text-cyan-400' : 'text-mono-500 hover:text-mono-300'
            }`}
          >
            SCATTER
          </button>
          <button
            onClick={() => setShowRegression(!showRegression)}
            className={`px-2 py-0.5 text-[10px] tracking-wider rounded transition-colors ${
              showRegression ? 'bg-cyan-900/30 text-cyan-400' : 'text-mono-500 hover:text-mono-300'
            }`}
          >
            TREND LINES
          </button>
        </div>
      </div>

      <WellSelectorPanel
        wells={wellItems}
        visibleWellIds={visibleWellIdSet}
        onToggle={(id) => {
          setHiddenWellIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          });
        }}
        onSelectAll={() => setHiddenWellIds(new Set())}
        onDeselectAll={() => setHiddenWellIds(new Set(data.wells.map((w) => w.well_id)))}
      />

      <AnalyteSelectorPanel
        analytes={allAnalytes}
        visibleCodes={visibleAnalyteCodes}
        onToggle={(code) => {
          const next = new Set(effectiveHidden);
          if (next.has(code)) next.delete(code);
          else next.add(code);
          setHiddenAnalytes(next);
        }}
        onSelectAll={() => setHiddenAnalytes(new Set())}
        onDeselectAll={() => setHiddenAnalytes(new Set(allAnalytes.map((a) => a.code)))}
      />

      <ChartTimeRangeRow
        dataMinMs={baseXDomain[0]}
        dataMaxMs={baseXDomain[1]}
        activeDomain={view.xDomain}
        onSelect={(d) => view.setXDomain(d)}
        isZoomed={view.isZoomed}
        onReset={view.reset}
      />

      <div ref={containerRef}>
        {points.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
            SELECT WELLS AND ANALYTES TO DISPLAY
          </div>
        ) : (
          <svg
            ref={svgRef}
            width={width || '100%'}
            height={CHART_HEIGHT}
            style={{ display: 'block', userSelect: 'none', touchAction: 'none', cursor: 'crosshair' }}
          >
            <defs>
              <clipPath id={clipId}>
                <rect x={MARGIN.left} y={MARGIN.top} width={innerWidth} height={innerHeight} />
              </clipPath>
            </defs>

            <g clipPath={`url(#${clipId})`}>
              {yTicks.map((t) => {
                const y = yScale(t);
                return (
                  <line
                    key={`grid-${t}`}
                    x1={MARGIN.left}
                    x2={MARGIN.left + innerWidth}
                    y1={y}
                    y2={y}
                    stroke="var(--dv-chart-grid)"
                    strokeDasharray="3 3"
                    strokeWidth={1}
                  />
                );
              })}

              {/* Regulatory lines */}
              {regulatoryLines.map((line) => {
                const v = convertConc(line.value, unit);
                if (v === null) return null;
                const [yLo, yHi] = view.yDomain;
                if (v > yHi || v < yLo) return null;
                const y = yScale(v);
                const dashArray = line.style === 'dashed' ? '8 4' : line.style === 'dotted' ? '2 4' : undefined;
                return (
                  <g key={line.key} style={{ filter: 'url(#dv-mcl-glow)' }}>
                    <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={y} y2={y} stroke={line.color} strokeWidth={1} strokeDasharray={dashArray} />
                    <text x={MARGIN.left + innerWidth - 4} y={y - 4} textAnchor="end" fontSize={9} fill={line.color}>
                      {`${line.label} ${line.analyteName}`}
                    </text>
                  </g>
                );
              })}

              {/* Regression lines */}
              {showRegression && regressions.map((r, i) => (
                <line
                  key={`reg-${i}`}
                  x1={xScale(r.x0)}
                  y1={yScale(r.y0)}
                  x2={xScale(r.x1)}
                  y2={yScale(r.y1)}
                  stroke={r.color}
                  strokeWidth={2}
                  strokeDasharray="6 3"
                />
              ))}

              {/* Scatter dots */}
              {showDots && points.map((p, i) => {
                const isHovered = hover?.point === p;
                return (
                  <circle
                    key={`pt-${i}`}
                    cx={xScale(p.ts)}
                    cy={yScale(p.value)}
                    r={isHovered ? 5 : 3}
                    fill={p.color}
                    opacity={0.85}
                    stroke="var(--dv-bg-card)"
                    strokeWidth={isHovered ? 2 : 1}
                    style={isHovered ? { filter: 'url(#dv-hover-halo)' } : undefined}
                  />
                );
              })}
            </g>

            {/* Y-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <g key={`yt-${t}`}>
                  <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatY(t)}
                  </text>
                </g>
              );
            })}
            <text transform={`translate(${MARGIN.left - 48}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
              Concentration (mg/L)
            </text>

            {/* X-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {xTicks.map((t, i) => {
              const x = xScale(t);
              return (
                <g key={`xt-${i}`}>
                  <line x1={x} x2={x} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight + 4} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={x} y={MARGIN.top + innerHeight + 16} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatX(t)}
                  </text>
                </g>
              );
            })}

            {view.rectPreview && (
              <rect
                x={view.rectPreview.x0}
                y={view.rectPreview.y0}
                width={view.rectPreview.x1 - view.rectPreview.x0}
                height={view.rectPreview.y1 - view.rectPreview.y0}
                fill="rgba(16, 185, 129, 0.12)"
                stroke="var(--dv-accent-emerald, #10b981)"
                strokeWidth={1}
                strokeDasharray="3 3"
                pointerEvents="none"
              />
            )}

            <rect
              x={MARGIN.left}
              y={MARGIN.top}
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            />
          </svg>
        )}
      </div>

      {hover && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 200, fontSize: 11 }}>
            <div style={{ fontSize: 10, color: 'var(--dv-tooltip-label, rgba(255,255,255,0.7))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              {new Date(hover.point.ts).toLocaleDateString()}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: hover.point.color }} />
              <span style={{ flex: 1 }}>{hover.point.series}</span>
              <span style={{ fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)', fontWeight: 600 }}>
                {hover.point.value.toFixed(4)} mg/L
              </span>
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
